import React from 'react';
import {
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  cn,
} from '@strivelabs/ui-core';
import type { AgentStep } from '../../types';
import { AgentStepDetail } from './AgentStepsBase';
import { TimelineStepDot } from './AgentThoughtsShared';

export type AgentThoughtsRunDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steps: AgentStep[];
  dialogTitle: string;
  dialogDescription?: string;
  renderStepContentOverride?: (step: AgentStep) => React.ReactNode;
  /**
   * When the dialog opens, scrolls this step (`AgentStep.id`) into view (e.g. from a key-finding Source pill).
   */
  initialScrollStepId?: string;
};

export function AgentThoughtsRunDialog({
  open,
  onOpenChange,
  steps: stepsProp,
  dialogTitle,
  dialogDescription,
  renderStepContentOverride,
  initialScrollStepId,
}: AgentThoughtsRunDialogProps) {
  const steps = stepsProp ?? [];

  React.useEffect(() => {
    if (!open || !initialScrollStepId?.trim()) return;
    const id = `agent-run-step-${initialScrollStepId.trim()}`;
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 120);
    return () => window.clearTimeout(t);
  }, [open, initialScrollStepId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="fullscreen"
        overlayClassName="bg-transparent"
        className={cn(
          'bg-black/80',
          '[&>button]:fixed [&>button]:right-4 [&>button]:top-4 [&>button]:z-[60] [&>button]:rounded-full [&>button]:border [&>button]:border-white/25 [&>button]:bg-white/10 [&>button]:p-2 [&>button]:text-white [&>button]:shadow-md [&>button]:hover:bg-white/20',
        )}
      >
        <DialogHeader className="mx-auto w-full max-w-3xl shrink-0 border-b border-white/10 px-6 py-4 pr-16 text-left">
          <DialogTitle className="text-white">{dialogTitle}</DialogTitle>
          {dialogDescription ? (
            <DialogDescription className="text-white/90">
              {dialogDescription}
            </DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="relative mx-auto w-full max-w-3xl">
            <ol className="relative list-none space-y-0 pl-0">
              <div
                className="pointer-events-none absolute left-3 top-3 bottom-3 z-0 w-px -translate-x-1/2 bg-zinc-400/80"
                aria-hidden
              />

              {steps.map((step) => {
                const decision = step.stepDecision;
                const showDecision =
                  decision &&
                  (Boolean(decision.finalized?.trim()) || Boolean(decision.next?.trim()));

                return (
                  <li
                    key={step.id}
                    id={`agent-run-step-${step.id}`}
                    className="relative scroll-mt-8 pb-8 last:pb-0"
                  >
                    <div className="flex gap-4">
                      <div className="relative z-[1] flex w-6 shrink-0 flex-col items-center">
                        <TimelineStepDot />
                      </div>

                      <Card
                        className={cn(
                          'min-w-0 flex-1 border-custom-border bg-background p-4 shadow-sm',
                          '[&_.text-muted-foreground]:text-zinc-600 dark:[&_.text-muted-foreground]:text-zinc-400',
                        )}
                      >
                        <div className="mb-3">
                          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                            {step.title}
                          </h3>
                          {step.subtitle ? (
                            <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
                              {step.subtitle}
                            </p>
                          ) : null}
                        </div>

                        <AgentStepDetail
                          step={step}
                          renderContentOverride={renderStepContentOverride}
                          className="border-0 bg-transparent p-0 shadow-none [&_.text-muted-foreground]:text-zinc-600 dark:[&_.text-muted-foreground]:text-zinc-400"
                        />
                      </Card>
                    </div>

                    {showDecision ? (
                      <div
                        className="mt-3 ml-10 space-y-2.5 border-l-2 border-white/30 pl-3"
                        role="note"
                        aria-label="Step decision"
                      >
                        {decision.finalized?.trim() ? (
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-white/55">
                              Finalized
                            </p>
                            <p className="mt-0.5 text-xs leading-snug text-white/90">
                              {decision.finalized.trim()}
                            </p>
                          </div>
                        ) : null}
                        {decision.next?.trim() ? (
                          <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-white/55">
                              Next
                            </p>
                            <p className="mt-0.5 text-xs leading-snug text-white/90">
                              {decision.next.trim()}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
